import {
  lazy,
  memo,
  Suspense,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';

import { useForm } from 'react-hook-form';
import toast from 'react-hot-toast';
import { FiSend, FiSmile } from 'react-icons/fi';
import Tippy from '@tippyjs/react';

import { Avatar } from '@components';
import { useAuth, useVideoCommentMutation } from '@hooks';
import { useEventfy } from '@hooks/useEventfy';
import { selectIsVideoCommentsModerationEnabled } from '@hooks/useEventfy/selectors';
import { selectUser } from '@hooks/useAuth/selectors';

import * as S from './styles';

const EmojiPicker = lazy(() => import('emoji-picker-react'));

type FormField = {
  comment: string;
};

type VideoNewCommentInputProps = {
  videoId: number;
  sortOrder: 'newest' | 'oldest';
};

const VideoNewCommentInput = ({
  videoId,
  sortOrder,
}: VideoNewCommentInputProps) => {
  const user = useAuth(selectUser);
  const hasModerationEnabled = useEventfy(
    selectIsVideoCommentsModerationEnabled,
  );

  const [emojiOpen, setEmojiOpen] = useState(false);
  const [emojiLoaded, setEmojiLoaded] = useState(false);

  const { handleSubmit, reset, register, setValue, getValues } =
    useForm<FormField>();

  const { mutate } = useVideoCommentMutation(
    videoId,
    hasModerationEnabled,
    sortOrder,
  );

  const inputRef = useRef<HTMLTextAreaElement>();

  const { ref, ...inputRegister } = register('comment', {
    required: true,
  });

  const handleRef = useCallback(
    (node: HTMLTextAreaElement | null) => {
      if (node) {
        ref(node);
        inputRef.current = node;
      }
    },
    [ref],
  );

  const onSubmit = handleSubmit(({ comment }) => {
    mutate(comment);
    reset({ comment: '' });

    if (hasModerationEnabled) {
      toast.success('Comentário enviado para aprovação da moderação.');
    } else {
      toast.success('Comentário publicado!');
    }
  });

  const handleEmojiSelect = useCallback(
    (emojiData: { emoji: string }) => {
      const textarea = inputRef.current;

      if (!textarea) return;

      const { selectionStart, selectionEnd } = textarea;
      const currentValue = getValues('comment') || '';
      const newValue =
        currentValue.slice(0, selectionStart) +
        emojiData.emoji +
        currentValue.slice(selectionEnd);

      setValue('comment', newValue, { shouldValidate: true });

      requestAnimationFrame(() => {
        const pos = selectionStart + emojiData.emoji.length;
        textarea.setSelectionRange(pos, pos);
        textarea.focus();
      });
    },
    [getValues, setValue],
  );

  const handleToggleEmoji = useCallback(() => {
    if (!emojiLoaded) setEmojiLoaded(true);
    setEmojiOpen((prev) => !prev);
  }, [emojiLoaded]);

  useEffect(() => {
    const textarea = inputRef.current;

    const handleEnterKeyPress = (e: HTMLElementEventMap['keydown']) => {
      if (e.key === 'Enter' && !e.shiftKey) {
        e.preventDefault();
        onSubmit();
      }
    };

    textarea?.addEventListener('keydown', handleEnterKeyPress);

    return () => {
      textarea?.removeEventListener('keydown', handleEnterKeyPress);
    };
  }, [onSubmit]);

  return (
    <S.Container>
      <Avatar src={user.avatar} alt={user.name} />

      <S.InputBox>
        <S.AutosizeInput
          placeholder="Adicione um comentário..."
          maxRows={3}
          {...inputRegister}
          ref={handleRef}
        />
        <Tippy
          content={
            emojiLoaded ? (
              <S.PickerWrapper>
                <Suspense fallback={null}>
                  <EmojiPicker
                    onEmojiClick={handleEmojiSelect}
                    width={320}
                    height={380}
                    searchPlaceholder="Buscar emoji..."
                    previewConfig={{ showPreview: false }}
                  />
                </Suspense>
              </S.PickerWrapper>
            ) : (
              <span />
            )
          }
          visible={emojiOpen}
          onClickOutside={() => setEmojiOpen(false)}
          interactive
          trigger="manual"
          placement="top-end"
          appendTo={document.body}
          animation={false}
        >
          <S.EmojiButton
            type="button"
            onClick={handleToggleEmoji}
            title="Emoji"
          >
            <FiSmile size="1rem" />
          </S.EmojiButton>
        </Tippy>
        <S.SendButton type="button" onClick={onSubmit} title="Enviar">
          <FiSend size="0.875rem" />
        </S.SendButton>
      </S.InputBox>
    </S.Container>
  );
};

export default memo(VideoNewCommentInput);
